import { api } from './client';
import type { OutgoingPaymentFields, OutgoingPayMethod } from './accounting.api';
import { i18nMap } from '@/lib/i18n-map';

export const CENTRAL_WAREHOUSE = 'central';

/** `medical_goods` = 8%, art. 96 lit. b) liniuța a patra; `medical_devices` = Registrul de stat. */
export type ItemVatRateKey = 'standard' | 'medical_devices' | 'medical_goods' | 'exempt';

export interface InventoryItem {
  id: string;
  name: string;
  unit: string | null;
  onHand: number;
  minStock: number;
  /** Order-up-to level; null uses the default (twice the minimum). */
  maxStock: number | null;
  /** At or below the reorder threshold. */
  low: boolean;
  price: number;
  value: number;
  category: string | null;
  supplierId: string | null;
  supplierName: string | null;
  location: string | null;
  barcode: string | null;
  /** Codul intern al clinicii (SKU). */
  sku: string | null;
  supplierCode: string | null;
  expiryDate: string | null;
  /** When it last came in from a supplier (an „achizitie" move); null if never. */
  lastReceivedAt: string | null;
  /**
   * A fact on the item, chosen in its form — not a guess from the category's
   * name (TRAT-10). The implant selectors on the patient chart and the
   * panoramic panel filter on this, with `implant=true`.
   */
  isImplant: boolean;
  /**
   * Implant attributes, for items in the implant category: a simulated implant
   * on a CBCT is drawn from these, and the patient's implant passport starts
   * from them.
   */
  implantBrand: string | null;
  implantSystem: string | null;
  implantDiameter: number | null;
  implantLength: number | null;
  /** What a person typed in as the weekly consumption; 0 when nobody has. */
  weeklyUsage: number;
  packSize: number | null;
  /**
   * Cum vinde furnizorul (Cutie, Pachet). Stocul rămâne în `unit`; o recepție
   * sau o comandă în unitatea asta se convertește cu `purchaseFactor`.
   */
  purchaseUnit: string | null;
  /** Câte `unit` intră într-o `purchaseUnit`. */
  purchaseFactor: number | null;
  branchId: string | null;
  /**
   * Cota TVA a materialului (`vat.rate.<cheie>`) și codul tarifar (NC) — ale
   * suplimentului de contabilitate: lipsesc din răspuns când e oprit.
   */
  vatRateKey?: ItemVatRateKey;
  tariffCode?: string | null;

  // Derived by the API from the consumption movements and the clinic's stock
  // parameters, so every screen divides the same way.

  /** The weekly figure actually used: the typed one, else the measured one. */
  usageWeekly: number;
  usageIsManual: boolean;
  /** The supplier's delivery time, or the clinic's default. */
  leadDays: number;
  /** Days of stock left at the current rate; null when nothing is consumed. */
  daysLeft: number | null;
  /** The threshold the consumption and the lead time imply. */
  suggestedMin: number | null;
  urgency: 'now' | 'soon' | 'ok';
}

/** The stock module's calculation parameters, from Setări → Stocuri. */
export interface StockParams {
  minCoverDays: number;
  defaultLead: number;
  reorderBuffer: number;
  usageWeeks: number;
  expiryWarnDays: number;
}

export const STOCK_PARAM_DEFAULTS: StockParams = {
  minCoverDays: 7,
  defaultLead: 3,
  reorderBuffer: 3,
  usageWeeks: 13,
  expiryWarnDays: 30,
};

export interface StockBalance {
  id: string;
  itemId: string;
  owner: string;
  qty: number;
  itemName: string | null;
  unit: string | null;
  minStock: number;
  price: number;
  value: number;
}

export type StockMoveKind =
  | 'achizitie'
  | 'livrare'
  | 'retur'
  | 'transfer'
  | 'consum'
  | 'casare'
  | 'inventar'
  | 'corectie'
  /** Marfă trimisă înapoi furnizorului. */
  | 'retur_furnizor';

/** Ce a ieșit și ce a intrat într-o fereastră, costat la prețul fiecărei mișcări. */
export interface ConsumptionAnalysis {
  outCost: number;
  inCost: number;
  outMoves: number;
  valueNow: number;
  valueInitial: number;
  /** Câte mișcări n-aveau preț propriu și au fost costate cu cel de azi. */
  withoutCost: number;
  top: Array<{ itemId: string; name: string; unit: string; qty: number; cost: number }>;
  /** Consumption cost per month across the window, oldest first. */
  trend: Array<{ month: string; cost: number }>;
}

export interface StockMove {
  id: string;
  itemId: string;
  itemName: string | null;
  from: string | null;
  to: string | null;
  qty: number;
  kind: StockMoveKind;
  reason: string | null;
  /** Prețul unitar de atunci (STOC-10); `null` la mișcările vechi. */
  unitCost: number | null;
  actor: string | null;
  refId: string | null;
  /** Doar pentru casări: a fost deja repusă în stoc. */
  undone?: boolean;
  at: string;
}

/** A ledger that disagrees with itself, by kind. */
export type StockIssue = 'negativ' | 'lot_diff' | 'fara_unitate' | 'fara_grupa';

export const STOCK_ISSUE_META: Record<
  StockIssue,
  { title: string; why: string; tone: 'danger' | 'warn' | 'info' }
> = {
  negativ: {
    title: 'Stoc negativ',
    why: 'Se consumă mai mult decât a intrat. Corectează printr-o inventariere sau o intrare cu motiv.',
    tone: 'danger',
  },
  lot_diff: {
    title: 'Mai mult în loturi decât pe stoc',
    why: 'Loturile adună mai mult decât cantitatea de pe stoc — consumul FEFO ar alege dintr-un lot care nu mai există. Un material urmărit doar parțial pe lot e normal; acesta nu.',
    tone: 'warn',
  },
  fara_unitate: {
    title: 'Fără unitate de măsură',
    why: 'Cantitățile nu se pot interpreta. Completează unitatea în fișa materialului.',
    tone: 'warn',
  },
  fara_grupa: {
    title: 'Fără grupă',
    why: 'Nu apar în filtrele pe grupă și lipsesc din rapoartele pe categorii.',
    tone: 'info',
  },
};

export interface StockAlerts {
  low: Array<{ id: string; name: string; unit: string | null; onHand: number; minStock: number }>;
  /** Not "order more" — "this ledger disagrees with itself". */
  integrity: Array<{ id: string; name: string; issue: StockIssue; detail: string }>;
  expiring: Array<{
    id: string;
    itemId: string;
    itemName: string;
    lotNo: string | null;
    qty: number;
    expiryDate: string;
    daysLeft: number;
    expired: boolean;
  }>;
}

export interface Supplier {
  id: string;
  name: string;
  contact: string | null;
  email: string | null;
  phone: string | null;
  deliveryDays: number;
  active: boolean;
  /** How many materials name this supplier, and how many are under minimum. */
  items: number;
  low: number;
  /** What that stock is worth now. */
  stockValue: number;
  /** Purchases recorded from them, what they came to, and the latest date. */
  buys: number;
  spent: number;
  lastBuy: string | null;
}

/** Un furnizor al unui material, cu prețul lui. */
export interface ItemSupplier {
  id: string;
  supplierId: string;
  supplierName: string | null;
  price: number;
  supplierCode: string | null;
  note: string | null;
}

export interface SupplierDebt {
  supplierId: string | null;
  supplierName: string | null;
  docNo: string | null;
  date: string;
  total: string;
  paid: string;
  rest: string;
  lines: string;
  ids: string[];
}

/**
 * Un depozit: centralul (`central`) sau al unei filiale (`branch:<id>`). Cabinetele
 * medicilor sunt tot gestiuni, dar nu depozite.
 */
export interface Warehouse {
  id: string;
  name: string;
  branchId: string | null;
}

/** Gestiunea e depozitul unei filiale. */
export const isBranchWarehouse = (owner: string | null | undefined) =>
  !!owner && owner.startsWith('branch:');

/** O plată făcută către un furnizor (istoricul plăților). */
export interface SupplierPaymentRow {
  id: string;
  supplierId: string | null;
  supplierName: string | null;
  docNo: string | null;
  amount: number;
  paidAt: string;
  actor: string | null;
  note: string | null;
  /** Rândul care rezumă ce se plătise înainte să existe istoricul. */
  backfilled: boolean;
  /** Ziua plății (§2.9), nu clipa în care s-a tastat. */
  paidOn: string;
  payMethod: OutgoingPayMethod | null;
  paymentDocNo: string | null;
  /** Rândul care stornează altul; suma lui e negativă. */
  stornoOf: string | null;
  stornoReason: string | null;
  reversed: boolean;
}

export interface Purchase {
  id: string;
  purchaseDate: string;
  docNo: string | null;
  supplierId: string | null;
  supplierName: string | null;
  itemId: string | null;
  itemName: string;
  unit: string;
  qty: number;
  unitPrice: number;
  total: number;
  paid: number;
  /** Ce mai e de plătit, după plăți și după ce s-a returnat furnizorului. */
  rest: number;
  /** Cât s-a trimis înapoi furnizorului de pe linie (unitatea de stoc). */
  returnedQty: number;
  returnedValue: number;
  /** Ce scria pe factură, când recepția s-a făcut în unitatea furnizorului (2 Cutie). */
  packUnit: string | null;
  packQty: number | null;
  lotNo: string | null;
  expiryDate: string | null;
}

export interface Stocktake {
  id: string;
  period: string;
  owner: string;
  ownerName: string | null;
  status: 'open' | 'done' | 'cancelled';
  startedBy: string | null;
  startedAt: string;
  closedBy: string | null;
  closedAt: string | null;
  /** Why the count was abandoned; only on a cancelled session. */
  cancelReason: string | null;
}

export interface StocktakeLine {
  id: string;
  itemId: string;
  itemName: string | null;
  unit: string | null;
  expected: number;
  counted: number | null;
  difference: number | null;
  price: number;
  note: string | null;
  /** De pe material, pentru numărarea cu scanerul. */
  barcode: string | null;
  location: string | null;
}

/** What was taken from stock for one appointment. */
export interface AppointmentConsumption {
  items: Array<{ itemId: string; itemName?: string; qty: number }>;
  byName: string | null;
  consumedAt: string;
}

export const inventoryApi = {
  consumption: (appointmentId: string) =>
    api.get<AppointmentConsumption | null>(`/inventory/consumption/${appointmentId}`),

  consume: (body: {
    appointmentId: string;
    owner: string;
    items: Array<{ itemId: string; itemName?: string; qty: number }>;
    service?: string | null;
  }) => api.post<void>('/inventory/consume', body),

  /** Put back what was written off for a visit, as a movement that says so. */
  revertConsumption: (appointmentId: string) =>
    api.delete<void>(`/inventory/consumption/${appointmentId}`),

  items: (
    params: {
      search?: string;
      category?: string;
      lowOnly?: boolean;
      owner?: string;
      page?: number;
      pageSize?: number;
      /** Ignores paging: the whole set at that level, not a page of it. */
      level?: 'critic' | 'low' | 'ok';
      /** Only implant fixtures — TRAT-10, in place of `category=implantologie`. */
      implant?: boolean;
    } = {},
  ) =>
    api.get<{
      items: InventoryItem[];
      total: number;
      page: number;
      pageSize: number;
      params: StockParams;
    }>('/inventory/items', { query: params }),

  /**
   * Tot catalogul, pagină cu pagină (STOC-6).
   *
   * Ecranele care aleg un material — inventarierea, recepția de facturi —
   * cereau prima pagină de 200 și atât: într-o clinică cu 250 de materiale,
   * ultimele cincizeci nu existau, fără ca nimic să spună asta. Serverul are
   * plafonul lui pe pagină; aici se cer paginile până se termină.
   */
  allItems: async (
    params: { search?: string; category?: string; owner?: string } = {},
  ): Promise<InventoryItem[]> => {
    const pageSize = 200;
    const out: InventoryItem[] = [];
    for (let page = 1; page <= 50; page++) {
      const res = await inventoryApi.items({ ...params, page, pageSize });
      out.push(...res.items);
      if (out.length >= res.total || res.items.length < pageSize) break;
    }
    return out;
  },

  /** The stock tiles' three numbers, over the whole catalogue — not a page of it. */
  counts: (params: { owner?: string; category?: string; search?: string } = {}) =>
    api.get<{ total: number; critic: number; low: number }>('/inventory/items/counts', {
      query: params,
    }),

  createItem: (body: Partial<InventoryItem>) => api.post<InventoryItem>('/inventory/items', body),
  /** Trece stocul unui material din unitatea mare în cea mică (Pachet → buc). */
  convertUnit: (id: string, body: { baseUnit: string; factor: number }) =>
    api.post<{ unit: string; purchaseUnit: string; purchaseFactor: number }>(
      `/inventory/items/${id}/convert-unit`,
      body,
    ),
  updateItem: (id: string, body: Partial<InventoryItem>) =>
    api.patch<InventoryItem>(`/inventory/items/${id}`, body),
  deleteItem: (id: string, reason?: string | null) =>
    api.delete<void>(`/inventory/items/${id}`, { query: reason ? { reason } : undefined }),
  /** Un import de materiale dintr-un fișier; ce există deja se sare. */
  importItems: (rows: Array<Record<string, unknown>>) =>
    api.post<{ added: number; skipped: number; names: string[] }>('/inventory/items/import', {
      rows,
    }),

  /** Scrie pragul propus pe fiecare material care are unul. */
  applyMins: (overwrite = false) =>
    api.post<{ changed: number }>('/inventory/items/apply-mins', { overwrite }),

  /** Leagă mai multe materiale de același furnizor. */
  assignSupplier: (itemIds: string[], supplierId: string | null) =>
    api.post<{ updated: number }>('/inventory/items/supplier', { itemIds, supplierId }),

  /** Repune în stoc o casare, ca o corecție care o citează. */
  undoScrap: (moveId: string, reason?: string) =>
    api.post<{ restored: number }>(`/inventory/moves/${moveId}/undo-scrap`, { reason }),

  /** Anulează o mișcare internă (livrare/retur/transfer): marfa se întoarce. */
  undoMove: (moveId: string, reason?: string) =>
    api.post<{ reversed: number }>(`/inventory/moves/${moveId}/undo`, { reason }),

  mergeItems: (keepId: string, mergeIds: string[]) =>
    api.post<{ merged: number }>('/inventory/items/merge', { keepId, mergeIds }),

  balances: (owner?: string) =>
    api.get<StockBalance[]>('/inventory/balances', { query: { owner } }),

  /**
   * Consumul perioadei, numărat pe server (STOC-2).
   *
   * Ecranul aducea o mie de mișcări și două sute de prețuri și înmulțea în
   * browser; restul materialelor intrau la preț 0.
   */
  consumptionAnalysis: (params: { from?: string; to?: string } = {}) =>
    api.get<ConsumptionAnalysis>('/inventory/consumption-analysis', { query: params }),

  moves: (
    params: {
      owner?: string;
      itemId?: string;
      kind?: StockMoveKind;
      from?: string;
      to?: string;
      limit?: number;
    } = {},
  ) => api.get<StockMove[]>('/inventory/moves', { query: params }),

  move: (body: {
    itemId: string;
    from?: string | null;
    to?: string | null;
    qty: number;
    kind: StockMoveKind;
    reason?: string | null;
  }) => api.post<void>('/inventory/moves', body),

  scrap: (body: {
    itemId: string;
    owner: string;
    qty: number;
    reason: string;
    note?: string | null;
  }) => api.post<void>('/inventory/scrap', body),

  alerts: () => api.get<StockAlerts>('/inventory/alerts'),

  /** Materials with stock but no consumption in the window — money on the shelf. */
  deadStock: (days = 90) =>
    api.get<
      Array<{
        id: string;
        name: string;
        unit: string | null;
        onHand: number;
        value: number;
        lastUse: string | null;
      }>
    >('/inventory/dead-stock', { query: { days } }),

  suppliers: (activeOnly = false) =>
    api.get<Supplier[]>('/inventory/suppliers', { query: { activeOnly } }),
  /** Furnizorii unui material, cu prețul fiecăruia, cel mai ieftin primul. */
  itemSuppliers: (itemId: string) =>
    api.get<ItemSupplier[]>(`/inventory/items/${itemId}/suppliers`),
  /** Înlocuiește toată lista de furnizori a unui material. */
  setItemSuppliers: (
    itemId: string,
    suppliers: Array<{
      supplierId: string;
      price: number;
      supplierCode?: string | null;
      note?: string | null;
    }>,
  ) => api.put<ItemSupplier[]>(`/inventory/items/${itemId}/suppliers`, { suppliers }),
  createSupplier: (body: Partial<Supplier>) => api.post<Supplier>('/inventory/suppliers', body),
  updateSupplier: (id: string, body: Partial<Supplier>) =>
    api.patch<Supplier>(`/inventory/suppliers/${id}`, body),

  purchases: (limit = 200) => api.get<Purchase[]>('/inventory/purchases', { query: { limit } }),
  purchaseUsage: (id: string) => api.get<PurchaseUsage>(`/inventory/purchases/${id}/usage`),
  /** Unde a ajuns marfa de pe o factură. */
  invoiceTrail: (docNo: string, supplierId?: string | null) =>
    api.get<InvoiceTrail>('/inventory/purchases/trail', { query: { docNo, supplierId } }),
  /** Media ponderată, ultimul preț și abaterea, pe material. */
  purchasePrices: (supplierId?: string | null) =>
    api.get<PurchasePrice[]>('/inventory/purchases/prices', { query: { supplierId } }),
  deletePurchase: (id: string) => api.delete<{ ok: boolean }>(`/inventory/purchases/${id}`),
  /** Trimite înapoi furnizorului o parte dintr-o linie de recepție. */
  returnToSupplier: (body: {
    purchaseId: string;
    qty: number;
    reason?: string | null;
    creditDocNo?: string | null;
    date?: string;
  }) => api.post<{ id: string; value: number; left: number }>('/inventory/purchases/return', body),
  /** Depozitul central și, cu mai multe filiale, câte unul pe filială. */
  warehouses: () => api.get<Warehouse[]>('/inventory/warehouses'),
  debts: () => api.get<SupplierDebt[]>('/inventory/debts'),
  /** Plățile făcute către furnizori, cele mai noi întâi (max 300). */
  supplierPayments: (query: { supplierId?: string; docNo?: string } = {}) =>
    api.get<SupplierPaymentRow[]>('/inventory/debts/payments', { query }),
  payInvoice: (ids: string[], amount: number, fields: OutgoingPaymentFields = {}) =>
    api.post<{ paid: number; total: number; rest: number }>('/inventory/debts/pay', {
      ids,
      amount,
      ...fields,
    }),
  /** Stornarea unei plăți către furnizor — un rând negativ, niciodată o ștergere. */
  reverseSupplierPayment: (id: string, reason: string) =>
    api.post<unknown>(`/inventory/debts/payments/${id}/reverse`, { reason }),

  receiveInvoice: (body: {
    date?: string;
    supplierId?: string | null;
    supplierName?: string | null;
    docNo?: string | null;
    note?: string | null;
    owner?: string;
    /**
     * Valuta facturii, trei litere mari (contabilitate §2.8). Implicit MDL; în
     * altă valută prețurile liniilor sunt în ea.
     */
    currency?: string | null;
    /** Lei pentru o unitate de valută; fără el, cursul BNM al zilei facturii. */
    fxRate?: number | null;
    /** Data facturii, când nu e ziua recepției. */
    invoiceDate?: string | null;
    lines: Array<{
      itemId?: string | null;
      itemName: string;
      unit?: string;
      qty: number;
      unitPrice: number;
      /** Cantitatea și prețul sunt în unitatea de cumpărare (cutie); serverul convertește. */
      inPacks?: boolean;
      lotNo?: string | null;
      expiryDate?: string | null;
      note?: string | null;
      /** TVA-ul liniei, în lei, cuprins în preț. */
      vatAmount?: number | null;
      /** Pentru ce livrări (art. 102 CF): exempt (implicit), taxable, mixed. */
      vatDestination?: 'exempt' | 'taxable' | 'mixed';
    }>;
    /** Comanda la furnizor din care vine marfa (STOC-8). */
    orderId?: string | null;
  }) => api.post<{ lines: number; total: number; ids: string[] }>('/inventory/invoices', body),

  stocktakes: () => api.get<Stocktake[]>('/inventory/stocktakes'),
  openStocktake: (body: { period: string; owner: string; ownerName?: string | null }) =>
    api.post<Stocktake>('/inventory/stocktakes', body),
  stocktakeLines: (id: string) => api.get<StocktakeLine[]>(`/inventory/stocktakes/${id}/lines`),
  saveCount: (lineId: string, counted: number | null, note?: string) =>
    api.patch<{ ok: boolean }>(`/inventory/stocktakes/lines/${lineId}`, { counted, note }),
  cancelStocktake: (id: string, reason?: string) =>
    api.post<{ ok: boolean }>(`/inventory/stocktakes/${id}/cancel`, { reason }),
  closeStocktake: (id: string, body: { scrapMissing?: boolean; scrapReason?: string } = {}) =>
    api.post<{ applied: number; surplus: number; shortfall: number }>(
      `/inventory/stocktakes/${id}/close`,
      body,
    ),
};

const MOVE_KIND_COMMON = i18nMap<Exclude<StockMoveKind, 'retur_furnizor'>>('common', {
  achizitie: 'achizitie',
  livrare: 'livrare',
  retur: 'retur',
  transfer: 'transfer',
  consum: 'consum',
  casare: 'casare',
  inventar: 'inventar',
  corectie: 'corectie',
});
/** Tipul nou își are eticheta în catalogul stocului, nu în cel comun. */
const MOVE_KIND_OWN = i18nMap<'retur_furnizor'>('inventory', { retur_furnizor: 'returLaFurnizor' });

export const MOVE_KIND_LABELS = new Proxy({} as Record<StockMoveKind, string>, {
  get: (_target, prop) =>
    prop === 'retur_furnizor'
      ? MOVE_KIND_OWN.retur_furnizor
      : (MOVE_KIND_COMMON as Record<string, string>)[prop as string],
});

export const SCRAP_REASONS = [
  { key: 'expirat', label: 'Expirat' },
  { key: 'deteriorat', label: 'Deteriorat' },
  { key: 'contaminat', label: 'Contaminat / nesteril' },
  { key: 'spart', label: 'Spart la manipulare' },
  { key: 'consumat', label: 'Consumat' },
  { key: 'pierdut', label: 'Pierdut' },
  { key: 'altul', label: 'Alt motiv' },
];

/** What is left of one receipt, asked before undoing it. */
export interface PurchaseUsage {
  itemName: string;
  unit: string | null;
  qty: number;
  /** False for an invoice line that was never matched to a material. */
  tracked: boolean;
  inStock: number;
  gone: number;
}

/** O mișcare din traseul unei linii de factură. */
export interface TrailMove {
  id: string;
  kind: string;
  qty: number;
  at: string;
  reason: string | null;
  /** Ce s-a întâmplat, în cuvinte. */
  label: string;
  /** A crescut stocul (intrare) sau l-a scăzut? */
  incoming: boolean;
}

export interface InvoiceTrail {
  docNo: string;
  lines: Array<{
    id: string;
    itemId: string | null;
    itemName: string;
    qty: number;
    unit: string | null;
    purchaseDate: string;
    /** False pentru o linie nelegată de nomenclator. */
    tracked: boolean;
    /** De ce nu e nimic de arătat, când nu e. */
    note: string | null;
    moves: TrailMove[];
  }>;
}

/** Cum s-a mișcat prețul unui material. */
export interface PurchasePrice {
  itemId: string | null;
  itemName: string;
  unit: string | null;
  buys: number;
  qty: number;
  avg: number;
  last: number;
  lastAt: string;
  min: number;
  max: number;
  /** Abaterea ultimului preț față de medie, %. Null la o singură achiziție. */
  drift: number | null;
}
